/**
 * `GET /version`, and the last answer kept on the device (plan.md step 54).
 *
 * The platform's part lives in `myrave:platform:version`; a rave's content
 * version in `myrave:{editionId}:version` (hard rule 6) — so the matrix opened
 * offline inside a rave still shows the last content version it knew.
 */
import { readJson, writeJson } from './storage';
import { noteServerReached } from './reach';

export interface ServerVersions {
  platform: { version: string };
  api: { version: string; sha: string; deployedAt: string | null };
  database: { migration: string | null };
  modules: Record<string, string>;
}

export interface EditionVersion {
  content: number | null;
  bundleVersion: string | null;
  publishedAt: string | null;
}

export interface Kept<T> { answer: T; fetchedAt: number }

export function keptServerVersions(): Kept<ServerVersions> | null {
  return readJson<Kept<ServerVersions> | null>(null, 'version', null);
}

export function keptEditionVersion(editionId: string): (EditionVersion & { fetchedAt: number }) | null {
  return readJson(editionId, 'version', null);
}

/** Ask the server; keep what it says. Null when it cannot be reached. */
export async function fetchVersions(apiBase: string, editionId: string | null, timeoutMs = 8_000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const query = editionId ? `?edition=${encodeURIComponent(editionId)}` : '';
    const res = await fetch(`${apiBase}/version${query}`, { cache: 'no-store', signal: controller.signal });
    noteServerReached();
    if (!res.ok) return null;
    const { edition, ...server } = (await res.json()) as ServerVersions & { edition: (EditionVersion & { id: string }) | null };
    const fetchedAt = Date.now();
    writeJson(null, 'version', { answer: server, fetchedAt });
    if (editionId && edition) {
      writeJson(editionId, 'version', {
        content: edition.content, bundleVersion: edition.bundleVersion, publishedAt: edition.publishedAt, fetchedAt,
      });
    }
    return { server, edition, fetchedAt };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
